import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ExperimentVM } from '../../../models/view-models/vm-experiment.model';
import { ProjectVM } from '../../../models/view-models/vm-projects.model';
import { MobileProjectsStateService } from '../../../services/mobile-projects-state.service';
import { IconComponent } from '../../../ui-component-library/core/icon.component/icon.component';
import { ProjectSearchComponent } from '../../../ui-component-library/project-search/project-search.component';

@Component({
  selector: 'app-mobile-projects',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatExpansionModule,
    MatIconModule,
    MatTooltipModule,
    IconComponent,
    ProjectSearchComponent,
  ],
  templateUrl: './mobile-projects.component.html',
  styleUrl: './mobile-projects.component.scss',
})
export class MobileProjectsComponent implements AfterViewInit, OnChanges {
  @Input() projects: ProjectVM[] = [];
  @Input() filteredProjects: ProjectVM[] = [];
  @Input() searchTerm = '';
  @Input() hasError = false;
  @Input() errorMessage = '';

  @Output() search = new EventEmitter<string>();

  expandedProjectIds: Set<string>;
  expandedExperimentKeys: Set<string>;
  private searchExpandedProjectIds = new Set<string>();
  private searchExpandedExperimentKeys = new Set<string>();
  private hasInitializedExpansion = false;
  private hasRestoredSelectedBatchScroll = false;

  @ViewChildren('batchRow', { read: ElementRef }) batchRows!: QueryList<ElementRef<HTMLElement>>;

  constructor(
    private readonly router: Router,
    private readonly stateService: MobileProjectsStateService
  ) {
    this.expandedProjectIds = new Set(stateService.expandedProjectIds);
    this.expandedExperimentKeys = new Set(stateService.expandedExperimentKeys);
    this.selectedBatchId = stateService.selectedBatchId;
  }

  selectedBatchId: string | null;

  ngAfterViewInit(): void {
    this.scheduleSelectedBatchScroll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !this.hasInitializedExpansion &&
      !this.stateService.hasSavedExpansionState &&
      changes['filteredProjects'] &&
      this.filteredProjects.length > 0
    ) {
      this.expandedProjectIds.add(String(this.filteredProjects[0].id_project ?? ''));
      this.hasInitializedExpansion = true;
    }

    if (changes['searchTerm'] || changes['filteredProjects']) {
      this.updateSearchExpansionState();
      this.scheduleSelectedBatchScroll();
    }
  }

  get totalExperiments(): number {
    return this.uniqueExperiments.length;
  }

  get openBatches(): number {
    return this.uniqueBatches.filter((batch) => this.isOpenBatch(batch.batchStatus)).length;
  }

  get batchesInProgress(): number {
    return this.uniqueBatches.filter((batch) => this.isInProgress(batch.batchStatus)).length;
  }

  getChildBatches(batch: any): any[] {
    return [...(batch?.childBatch ?? [])].sort((first, second) =>
      this.batchSequenceCompare(first?.batchSequence, second?.batchSequence)
    );
  }

  batchDisplayName(batch: any, index: number): string {
    const batchVersion = this.batchVersionLabel(batch, index);
    const batchTitle = this.batchTitleLabel(batch);
    return batchTitle ? `${batchVersion} (${batchTitle})` : batchVersion;
  }

  batchVersionLabel(batch: any, index: number): string {
    return batch?.batchVersion || `Batch ${String(index + 1).padStart(2, '0')}`;
  }

  batchTitleLabel(batch: any): string {
    return batch?.batchTitle || '';
  }

  openBatchNotes(batch: any, parentBatch: any = batch): void {
    this.selectedBatchId = String(batch?.id ?? '');
    this.stateService.selectedBatchId = this.selectedBatchId;
    this.saveExpansionState();
    const batchVersions = this.getNoteBatchVersions(batch, parentBatch);
    const batchIds = batch?.id ? [batch.id] : [];
    this.router.navigate(['/batches', batch?.id, 'notes'], {
      queryParams: {
        batchIds: batchIds.join(','),
        batchVersions: batchVersions.join(','),
        batchTitle: this.batchDisplayName(batch, 0),
      },
      state: { batch, parentBatch, batchVersions },
    });
  }

  private getNoteBatchVersions(batch: any, parentBatch: any): string[] {
    const parentVersion = this.batchVersionLabel(parentBatch, 0);
    const batchVersion = this.batchVersionLabel(batch, 0);

    return String(batch?.id) === String(parentBatch?.id)
      ? [parentVersion]
      : [...new Set([parentVersion, batchVersion])];
  }

  getProjectExperiments(project: ProjectVM): Partial<ExperimentVM>[] {
    return [...(project.project_experiments ?? [])].sort((first, second) =>
      this.latestFirst(first.experimentModifiedDate, second.experimentModifiedDate)
    );
  }

  getExperimentBatches(experiment: Partial<ExperimentVM>): any[] {
    return [...(experiment.experimentBatches ?? [])]
      .filter((batch) => !batch.parentBatchUuid)
      .sort((first, second) => this.latestFirst(first.batchModifiedDate, second.batchModifiedDate));
  }

  getExperimentKey(project: ProjectVM, experiment: Partial<ExperimentVM>): string {
    return `${project.id_project}:${experiment.id ?? experiment.experimentTitle ?? ''}`;
  }

  isExperimentExpanded(project: ProjectVM, experiment: Partial<ExperimentVM>): boolean {
    const experimentKey = this.getExperimentKey(project, experiment);
    return this.expandedExperimentKeys.has(experimentKey) || this.searchExpandedExperimentKeys.has(experimentKey);
  }

  isProjectExpanded(project: ProjectVM): boolean {
    const projectId = String(project.id_project ?? '');
    return this.expandedProjectIds.has(projectId) || this.searchExpandedProjectIds.has(projectId);
  }

  openProject(project: ProjectVM): void {
    const projectId = String(project.id_project ?? '');
    if (!this.searchExpandedProjectIds.has(projectId)) {
      this.expandedProjectIds.add(projectId);
      this.saveExpansionState();
    }
  }

  closeProject(project: ProjectVM): void {
    const projectId = String(project.id_project ?? '');
    this.expandedProjectIds.delete(projectId);
    this.saveExpansionState();
  }

  openExperiment(project: ProjectVM, experiment: Partial<ExperimentVM>): void {
    const experimentKey = this.getExperimentKey(project, experiment);
    if (!this.searchExpandedExperimentKeys.has(experimentKey)) {
      this.expandedExperimentKeys.add(experimentKey);
      this.saveExpansionState();
    }
  }

  closeExperiment(project: ProjectVM, experiment: Partial<ExperimentVM>): void {
    this.expandedExperimentKeys.delete(this.getExperimentKey(project, experiment));
    this.saveExpansionState();
  }

  isBatchSelected(batch: any): boolean {
    return !!this.selectedBatchId && String(batch?.id ?? '') === this.selectedBatchId;
  }

  statusTitle(status: any): string {
    return String(status?.appStatusTitle ?? status ?? 'Active');
  }

  isInProgress(status: any): boolean {
    return this.statusTitle(status).toLowerCase().includes('active');
  }

  isOpenBatch(status: any): boolean {
    const normalized = this.statusTitle(status).toLowerCase();
    return !['complete', 'completed', 'closed', 'archived', 'archive', 'cancelled', 'canceled'].some((value) =>
      normalized.includes(value)
    );
  }

  trackByProjectId(index: number, project: ProjectVM): string {
    return String(project?.id_project ?? index);
  }

  trackByBatchId(index: number, batch: any): string {
    return String(batch?.id ?? batch?.batchVersion ?? index);
  }

  private get uniqueExperiments(): Partial<ExperimentVM>[] {
    const seen = new Set<string>();
    return this.projects
      .flatMap((project) => this.getProjectExperiments(project))
      .filter((experiment) => {
        const key = String(experiment?.id ?? experiment?.experimentTitle ?? '');
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  private get uniqueBatches(): any[] {
    const seen = new Set<string>();
    return this.uniqueExperiments
      .flatMap((experiment) =>
        this.getExperimentBatches(experiment).flatMap((batch) => [batch, ...(batch.childBatch ?? [])])
      )
      .filter((batch) => {
        const key = String(batch?.id ?? batch?.batchVersion ?? batch?.batchTitle ?? '');
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  private latestFirst(
    firstDate: Date | string | null | undefined,
    secondDate: Date | string | null | undefined
  ): number {
    const firstTime = this.dateValue(firstDate);
    const secondTime = this.dateValue(secondDate);
    return secondTime - firstTime;
  }

  private dateValue(date: Date | string | null | undefined): number {
    if (!date) return -Infinity;
    const value = new Date(date).getTime();
    return Number.isFinite(value) ? value : -Infinity;
  }

  private batchSequenceCompare(
    firstSequence: number | null | undefined,
    secondSequence: number | null | undefined
  ): number {
    const firstValue = Number.isFinite(Number(firstSequence)) ? Number(firstSequence) : Infinity;
    const secondValue = Number.isFinite(Number(secondSequence)) ? Number(secondSequence) : Infinity;
    return firstValue - secondValue;
  }

  private updateSearchExpansionState(): void {
    const searchTerm = this.searchTerm.trim().toLowerCase();
    this.searchExpandedProjectIds.clear();
    this.searchExpandedExperimentKeys.clear();

    if (!searchTerm) return;

    this.filteredProjects.forEach((project) => {
      const matchingExperiments = this.getProjectExperiments(project).filter((experiment) =>
        this.experimentMatchesSearch(experiment, searchTerm)
      );

      if (matchingExperiments.length > 0) {
        this.searchExpandedProjectIds.add(String(project.id_project ?? ''));
        matchingExperiments.forEach((experiment) =>
          this.searchExpandedExperimentKeys.add(this.getExperimentKey(project, experiment))
        );
      }
    });
  }

  private saveExpansionState(): void {
    this.stateService.saveExpansionState(this.expandedProjectIds, this.expandedExperimentKeys);
  }

  private scheduleSelectedBatchScroll(): void {
    if (this.hasRestoredSelectedBatchScroll || !this.selectedBatchId) return;

    setTimeout(() => {
      const selectedRow = this.batchRows?.find((row) => row.nativeElement.dataset['batchId'] === this.selectedBatchId);
      if (!selectedRow) return;

      selectedRow.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.hasRestoredSelectedBatchScroll = true;
    });
  }

  private experimentMatchesSearch(experiment: Partial<ExperimentVM>, searchTerm: string): boolean {
    if ((experiment.experimentTitle || '').toLowerCase().includes(searchTerm)) return true;

    return (experiment.experimentBatches || []).some((batch) => this.batchMatchesSearch(batch, searchTerm));
  }

  private batchMatchesSearch(batch: any, searchTerm: string): boolean {
    return (
      String(batch?.batchVersion ?? '')
        .toLowerCase()
        .includes(searchTerm) ||
      String(batch?.batchTitle ?? '')
        .toLowerCase()
        .includes(searchTerm) ||
      (batch?.childBatch || []).some((child: any) => this.batchMatchesSearch(child, searchTerm))
    );
  }
}
